"use client";

import React from "react";
import { Phone, Mail, MapPin, MessageCircle, Facebook, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({
  className,
  type,
  ...props
}, ref) => {
  return <input type={type} className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`} ref={ref} {...props} />;
});
Input.displayName = "Input";
const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(({
  className,
  ...props
}, ref) => {
  return <textarea className={`flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`} ref={ref} {...props} />;
});
Textarea.displayName = "Textarea";
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-6 bg-accent/10 border border-accent/20 rounded-md text-center">
                <p className="text-foreground font-medium mb-4">{successMessage}</p>
                <Button onClick={resetForm} variant="outline" className="rounded-md border-primary text-primary hover:bg-primary hover:text-primary-foreground">
                    Send Another Request
                </Button>
            </div>;
  }
  return <Form onSubmit={handleSubmit}>
            <div className="space-y-5">
                <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="font-default text-foreground">Full Name</FormLabel>
                            <FormControl>
                                <Input placeholder="Dustin Royer" className="rounded-md border-border focus-visible:ring-accent" {...field} />
                            </FormControl>
                            <p className="text-[0.8rem] text-muted-foreground">Please enter your full name</p>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="font-default text-foreground">Email Address</FormLabel>
                            <FormControl>
                                <Input type="email" placeholder="hello@example.com" className="rounded-md border-border focus-visible:ring-accent" {...field} />
                            </FormControl>
                            <p className="text-[0.8rem] text-muted-foreground">We will reply to this address</p>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="event_details" rules={{
        required: "Event details are required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="font-default text-foreground">Event Date & Location</FormLabel>
                            <FormControl>
                                <Input type="text" placeholder="Oct 12, 2024 - Nashville, TN" className="rounded-md border-border focus-visible:ring-accent" {...field} />
                            </FormControl>
                            <p className="text-[0.8rem] text-muted-foreground">Proposed date and venue location</p>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="message" rules={{
        required: "Message is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="font-default text-foreground">Ministry Request Details</FormLabel>
                            <FormControl>
                                <Textarea placeholder="Tell us about your event and audience size..." className="min-h-[120px] rounded-md border-border focus-visible:ring-accent" {...field} />
                            </FormControl>
                            <p className="text-[0.8rem] text-muted-foreground">Include any specific topics or themes</p>
                            <FormMessage />
                        </FormItem>} />

                {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                        <p className="text-sm text-destructive">{submitError}</p>
                    </div>}

                <Button type="submit" disabled={isSubmitting} className="w-full h-11 text-base bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground transition-colors rounded-md font-default">
                    {isSubmitting ? <>
                            <Loader className="mr-2 h-5 w-5 animate-spin" />
                            Sending Request...
                        </> : 'Submit Invitation'}
                </Button>
            </div>
        </Form>;
}
export default function ContactUs() {
  return <section data-section-id="251282" id="invite-dustin" className="py-16 md:py-24 bg-card">
            <div className="container mx-auto px-4 md:px-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
                    
                    <div className="space-y-10">
                        <div>
                            <h2 className="text-3xl md:text-5xl font-bold font-serif text-foreground mb-4 tracking-tight">
                                Invite Dustin to Speak
                            </h2>
                            <p className="text-lg text-muted-foreground leading-relaxed font-default">
                                Dustin is passionate about sharing the message of purpose-rooted living at churches, conferences, and retreats. Use the form to start a conversation about your upcoming event.
                            </p>
                        </div>

                        <div className="space-y-6">
                            <div className="flex items-start gap-4">
                                <div className="bg-accent/10 p-3 rounded-md text-accent shrink-0">
                                    <Phone className="w-6 h-6" />
                                </div>
                                <div>
                                    <h3 className="font-semibold font-default text-lg text-foreground">Direct Line</h3>
                                    <p className="text-muted-foreground mt-1 text-lg font-default">+1 (555) 123-4567</p>
                                </div>
                            </div>

                            <div className="flex items-start gap-4">
                                <div className="bg-accent/10 p-3 rounded-md text-accent shrink-0">
                                    <Mail className="w-6 h-6" />
                                </div>
                                <div>
                                    <h3 className="font-semibold font-default text-lg text-foreground">Ministry Inquiries</h3>
                                    <p className="text-muted-foreground mt-1 text-lg font-default">speaking@dustinroyer.com</p>
                                </div>
                            </div>

                            <div className="flex items-start gap-4">
                                <div className="bg-accent/10 p-3 rounded-md text-accent shrink-0">
                                    <MapPin className="w-6 h-6" />
                                </div>
                                <div>
                                    <h3 className="font-semibold font-default text-lg text-foreground">Office Location</h3>
                                    <p className="text-muted-foreground mt-1 text-lg font-default">Nashville, Tennessee</p>
                                </div>
                            </div>
                        </div>

                        <div className="pt-8 border-t border-border space-y-5">
                            <h3 className="font-semibold font-default text-xl text-foreground">Connect on Social</h3>
                            <div className="flex flex-col sm:flex-row gap-4">
                                <Button className="flex-1 bg-primary hover:bg-accent text-primary-foreground shadow-md h-12 text-base rounded-md transition-all" asChild>
                                    <Link to="https://wa.me/15551234567" newTab>
                                        <MessageCircle className="mr-2 w-5 h-5" />
                                        WhatsApp
                                    </Link>
                                </Button>
                                <Button className="flex-1 bg-secondary hover:bg-primary text-secondary-foreground shadow-md h-12 text-base rounded-md transition-all" asChild>
                                    <Link to="https://facebook.com" newTab>
                                        <Facebook className="mr-2 w-5 h-5" />
                                        Facebook Page
                                    </Link>
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div className="lg:pl-4">
                        <Card className="shadow-xl border-border/50 bg-background/80 backdrop-blur-sm rounded-md">
                            <CardHeader className="pb-6">
                                <CardTitle className="text-2xl font-serif text-foreground">Invitation Form</CardTitle>
                                <CardDescription className="text-base font-default">
                                    Please provide details about your event and we will get back to you shortly.
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <FormProvider formKey="invite_form_speaking" formId="invite_form_speaking" sectionName="contact-us" defaultValues={{
                name: "",
                email: "",
                event_details: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                event_details: {
                  required: true
                },
                message: {
                  required: true
                }
              }} submitText="Submit Invitation" successMessage="Thank you! Your invitation has been received. Dustin's team will review the details and contact you soon." errorMessage="There was an error submitting your request. Please try again or email us directly.">
                                    <ContactFormInner />
                                </FormProvider>
                            </CardContent>
                        </Card>
                    </div>

                </div>
            </div>
        </section>;
}